<script lang="ts">
  import { Button, MultiSelect, Select, SelectItem, Toolbar, ToolbarContent, ToolbarSearch } from 'carbon-components-svelte';
  import noop from 'lodash/noop';

  import type { TableFilter } from './table-types';

  export let filters: TableFilter[] = [],
    filterValues: Record<string, string[]> = {},
    size: 'compact' | 'short' | 'medium' | 'tall' = 'medium',
    searchable = true,
    searchPlaceholder = 'Search',
    searchText = '',
    exportable = false,
    canExport = false,
    expandable = false,
    expandAllToggle = false,
    allExpanded = false,
    expandDisabled = false,
    controlsDisabled = false,
    onFilterSelect: (key: string, values: string[]) => void = noop,
    onSearch: (event: Event) => void = noop,
    onToggleExpandAll: () => void = noop,
    onOpenExport: () => void = noop;

  const FILTER_SIZE_BY_TABLE_SIZE: Record<'compact' | 'short' | 'medium' | 'tall', 'sm' | 'xl' | undefined> = {
    compact: 'sm',
    short: 'sm',
    medium: undefined,
    tall: 'xl',
  };
  $: filterSize = FILTER_SIZE_BY_TABLE_SIZE[size];
  $: filterSizeProps = filterSize ? { size: filterSize } : {};
</script>

<Toolbar class="mst-toolbar-fit">
  <ToolbarContent>
    {#each filters as filter (filter.key)}
      <div class="mst-filter">
        {#if filter.multiple}
          <MultiSelect
            {...filterSizeProps}
            disabled={controlsDisabled}
            label={filter.label}
            items={filter.options.map((option) => ({ id: option.value, text: option.text }))}
            selectedIds={filterValues[filter.key] ?? []}
            on:select={(event) => onFilterSelect(filter.key, event.detail.selectedIds)}
          />
        {:else}
          <Select
            inline
            hideLabel
            {...filterSizeProps}
            disabled={controlsDisabled}
            labelText={filter.label}
            selected={(filterValues[filter.key] ?? [])[0] ?? ''}
            on:update={(event) => onFilterSelect(filter.key, event.detail ? [event.detail] : [])}
          >
            <SelectItem value="" text={filter.label} />
            {#each filter.options as option (option.value)}
              <SelectItem value={option.value} text={option.text} />
            {/each}
          </Select>
        {/if}
      </div>
    {/each}
    {#if searchable}
      <ToolbarSearch
        placeholder={searchPlaceholder}
        persistent
        disabled={controlsDisabled}
        value={searchText}
        on:clear={onSearch}
        on:input={onSearch}
      />
    {/if}
    {#if expandable && expandAllToggle}
      <Button kind="ghost" size="small" disabled={expandDisabled} on:click={onToggleExpandAll}>
        {allExpanded ? 'Collapse all' : 'Expand all'}
      </Button>
    {/if}
    {#if exportable}
      <Button kind="secondary" size="small" disabled={!canExport} on:click={onOpenExport}>Export CSV</Button>
    {/if}
  </ToolbarContent>
</Toolbar>

<style>
  .mst-filter {
    display: flex;
    align-items: center;
  }

  .mst-filter :global(.mst-filter__icon) {
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  .mst-filter :global(.bx--multi-select) {
    min-width: 11rem;
  }

  .mst-filter :global(.bx--list-box) {
    border-bottom: none;
  }

  .mst-filter :global(.bx--list-box__wrapper--inline) {
    grid-gap: 0;
  }

  @media (max-width: 42rem) {
    :global(.bx--table-toolbar.mst-toolbar-fit),
    :global(.mst-toolbar-fit .bx--toolbar-content) {
      height: auto;
    }

    :global(.mst-toolbar-fit .bx--toolbar-content) {
      flex-direction: column;
      align-items: stretch;
    }

    :global(.mst-toolbar-fit .bx--toolbar-content > *) {
      width: 100%;
    }

    :global(.mst-toolbar-fit .mst-filter .bx--list-box__wrapper) {
      width: 100%;
    }

    :global(.mst-toolbar-fit .bx--toolbar-content .bx--btn) {
      max-width: unset;
      justify-content: center;
      padding: calc(0.375rem - 3px) 16px;
    }
  }
</style>
